/**
 * Dost draft-content dialog (Phase 4.5-C, admin-only).
 *
 * Used inside VisualBlockEditor for text-type blocks. Grounded on the event
 * context (server-side fetches the event + existing blocks by event_id).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Sparkles, RefreshCcw, Check, Pencil, RotateCcw, X } from 'lucide-react';
import api from '@/utils/api';

const TONES = [
  { value: 'warm', label: 'Warm' },
  { value: 'confident', label: 'Confident' },
  { value: 'casual', label: 'Casual' },
  { value: 'thoughtful', label: 'Thoughtful' },
  { value: 'playful', label: 'Playful' },
  { value: 'concise', label: 'Concise' },
];

const DostDraftDialog = ({ open, onClose, eventId, blockType, onAccept }) => {
  const [tone, setTone] = useState('warm');
  const [instruction, setInstruction] = useState('');
  const [phase, setPhase] = useState('form'); // form | loading | result | error
  const [draft, setDraft] = useState('');
  const [editedDraft, setEditedDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [meta, setMeta] = useState({ latency_ms: 0, tokens_used: 0 });
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (open) {
      setPhase('form');
      setDraft(''); setEditedDraft(''); setInstruction('');
      setEditing(false); setErrMsg('');
    }
  }, [open]);

  const run = async () => {
    setPhase('loading'); setErrMsg('');
    try {
      const { data } = await api.post('/dost/admin/draft-content', {
        event_id: eventId,
        block_type: blockType,
        tone,
        instruction,
      });
      setDraft(data.draft_text || '');
      setEditedDraft(data.draft_text || '');
      setMeta({ latency_ms: data.latency_ms || 0, tokens_used: data.tokens_used || 0 });
      setPhase('result');
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || 'Dost is having a moment. Try again shortly.');
      setPhase('error');
    }
  };

  const accept = () => {
    onAccept?.(editing ? editedDraft : draft);
    onClose?.();
  };

  const isEmpty = !instruction.trim();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <DialogTitle>Draft with Dost</DialogTitle>
          </div>
          <p className="text-xs text-neutral-500 mt-1">
            Block type: <span className="font-mono text-primary-800">{blockType}</span>.
            {' '}Dost uses your event basics for grounding.
          </p>
        </DialogHeader>

        {phase === 'form' && (
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Tone</div>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="draft-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">What should this section say?</div>
              <Textarea
                rows={4}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder="e.g. 3 bullets, hint at intentional connection, no venue details"
                data-testid="draft-instruction-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={run} disabled={isEmpty} className="bg-primary-800 hover:bg-primary-900 text-white" data-testid="draft-run-btn">
                <Sparkles className="h-4 w-4 mr-1.5" /> Draft with Dost
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'loading' && (
          <div className="py-10 flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center animate-pulse">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="text-sm text-primary-900 font-medium">Dost is drafting...</div>
          </div>
        )}

        {phase === 'result' && (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-secondary-700">Draft</div>
                {!editing ? (
                  <button onClick={() => setEditing(true)} className="text-xs text-primary-800 hover:text-primary-900 flex items-center gap-1">
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                ) : (
                  <button onClick={() => setEditing(false)} className="text-xs text-primary-800 hover:text-primary-900 flex items-center gap-1">Done</button>
                )}
              </div>
              {editing ? (
                <Textarea rows={10} value={editedDraft} onChange={(e) => setEditedDraft(e.target.value)} className="text-sm" data-testid="draft-edit-area" />
              ) : (
                <div className="p-3 rounded-lg bg-secondary-50/40 border border-secondary-200 text-sm text-primary-900 whitespace-pre-wrap min-h-[180px] max-h-[300px] overflow-auto" data-testid="draft-result">
                  {draft || <span className="text-neutral-400 italic">No output</span>}
                </div>
              )}
            </div>
            <div className="text-[11px] text-neutral-500 flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">tone: {tone}</Badge>
              <span>{meta.latency_ms}ms</span>
              <span>·</span>
              <span>~{meta.tokens_used} tokens</span>
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" /> Cancel</Button>
              <Button variant="outline" onClick={() => setPhase('form')}><RotateCcw className="h-4 w-4 mr-1" /> New instruction</Button>
              <Button variant="outline" onClick={run}><RefreshCcw className="h-4 w-4 mr-1" /> Regenerate</Button>
              <Button onClick={accept} className="bg-primary-800 hover:bg-primary-900 text-white" data-testid="draft-accept-btn">
                <Check className="h-4 w-4 mr-1" /> Use this draft
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'error' && (
          <div className="py-8 flex flex-col items-center gap-3">
            <div className="text-sm text-primary-900 text-center">{errMsg}</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button onClick={run} className="bg-primary-800 hover:bg-primary-900 text-white">Try again</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DostDraftDialog;
